import {
  Col,
  DefineTermDescription,
  Divider,
  Flex,
  Row,
  Space,
  Text,
  theme,
} from '@/components';
import { PinpointMarker } from '@/features/comment/components/pinpoint';
import {
  type PinpointSheetType,
  getPinpointSceneAndFieldBySheetType,
} from '@/features/comment/utils/pinpoint';
import { useMinistries } from '@/features/master-table/hooks';
import { getMinistryName } from '@/features/mold/utils/formatter/rs-ledger';
import type {
  FundAdditionalAttributesModel,
  ProjectBaseModel,
} from '@/features/project/projects/_models/project';
import { useSnapshot } from '@/features/tenant-manager/snapshots/_hooks/useSnapshot';
import { useId } from '@/hooks';
import { FALLBACK_TEXT, ITEM_SPLITTER, isPS } from '@/utils';
import { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

type ProjectType = z.infer<typeof ProjectBaseModel>;

type Props = {
  ministryId: string;
  organization: ProjectType['organization'];
  relatedOrganizations: ProjectType['relatedOrganizations'];
  otherResponsibleOrganizations: ProjectType['otherResponsibleOrganizations'];
  peopleInCharge: ProjectType['peopleInCharge'];
  businessManagers: ProjectType['businessManagers'];

  historySavedAt: string | undefined;
  snapshotId: string | undefined | null;
} & (
  | {
      pinpointSheetType: Exclude<PinpointSheetType, 'KS_STANDARD'>;
    }
  | {
      pinpointSheetType: 'KS_STANDARD';
      additionalJurisdictionalUsers: z.infer<
        typeof FundAdditionalAttributesModel.shape.additionalJurisdictionalUsers
      >;
      additionalJurisdictionalOrganizations:
        | ProjectType['additionalJurisdictionalOrganizations']
        | undefined;
      additionalJurisdictionalBusinessManagers:
        | ProjectType['additionalJurisdictionalBusinessManagers']
        | undefined;
      additionalJurisdictionalPeopleInCharge:
        | ProjectType['additionalJurisdictionalPeopleInCharge']
        | undefined;
    }
);

export const ProjectInformationHeader: FC<Props> = (props) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { id } = useId();

  const snapshot = useSnapshot({ id: props.snapshotId });

  const nestedOrganizations = useMemo(() => {
    return props.organization.names.join(ITEM_SPLITTER);
  }, [props.organization]);

  const nestedRelatedOrganizationNames = useMemo(() => {
    return props.relatedOrganizations.map((organization) => {
      return organization.names.join(ITEM_SPLITTER);
    });
  }, [props.relatedOrganizations]);

  const ministries = useMinistries();

  const peopleInChargesNames = useMemo(() => {
    return props.peopleInCharge.map((person) => {
      return [person.name, ...person.organizations.map((v) => v.name)]
        .filter(Boolean)
        .join(ITEM_SPLITTER);
    });
  }, [props.peopleInCharge]);

  const businessManagerNames = useMemo(() => {
    return props.businessManagers.map((person) => {
      return [person.name, ...person.organizations.map((v) => v.name)]
        .filter(Boolean)
        .join(ITEM_SPLITTER);
    });
  }, [props.businessManagers]);

  const nestedOtherResponsibleOrganizationNames = useMemo(() => {
    return props.otherResponsibleOrganizations.map((organization) => {
      return organization.names.join(ITEM_SPLITTER);
    });
  }, [props.otherResponsibleOrganizations]);

  const additionalJurisdictionalUsers = useMemo(() => {
    if (props.pinpointSheetType !== 'KS_STANDARD') {
      return [];
    }

    return (
      props.additionalJurisdictionalUsers?.map((value) => {
        return {
          ministryId: value.ministryId,
          organizationName: props.additionalJurisdictionalOrganizations
            ?.find((v) => v.id === value.organizationId)
            ?.names.join(ITEM_SPLITTER),
          peopleInChargeNames: value.peopleInChargeIds.map((id) => {
            return props.additionalJurisdictionalPeopleInCharge?.find(
              (v) => v.id === id,
            )?.name;
          }),
          businessManagerNames: value.businessManagerIds.map((id) => {
            return props.additionalJurisdictionalBusinessManagers?.find(
              (v) => v.id === id,
            )?.name;
          }),
        };
      }) ?? []
    );
  }, [props]);

  return (
    <Flex
      vertical
      style={{ backgroundColor: token.colorBgLayout, padding: 16 }}
      gap={12}
    >
      <Row gutter={[24, 12]}>
        <Col span={12}>
          <Space
            split={
              <Divider
                type="vertical"
                size="md"
                style={{ backgroundColor: token.colorBorder }}
              />
            }
          >
            <Text color="secondary" text={t('organization-in-charge')} />
            <PinpointMarker
              pinpoint={getPinpointSceneAndFieldBySheetType({
                pinpointSheetType: props.pinpointSheetType,
                rs: {
                  field: 'rs.organization-in-charge',
                  path: { project: id },
                },
                rsSkippedReview: {
                  field: 'rs.skippedReview.organization-in-charge',
                  path: { project: id },
                },
                ss: {
                  field: 'ss.organization-in-charge',
                  path: { project: id },
                },
                ksInvestment: {
                  field: 'ks.investment.organization-in-charge',
                  path: { project: id },
                },
                ksStandard: {
                  field: 'ks.standard.organization-in-charge',
                  path: { project: id },
                },
                ksLocalGovernment: {
                  field: 'ks.local-government.organization-in-charge',
                  path: { project: id },
                },
              })}
            >
              <Space>
                <Text
                  color="secondary"
                  text={getMinistryName({
                    targetId: props.ministryId,
                    ministries: ministries.data,
                  })}
                />
                {nestedOrganizations && (
                  <Text
                    color="secondary"
                    variant="footnote"
                    text={`[${nestedOrganizations}]`}
                  />
                )}
              </Space>
            </PinpointMarker>
          </Space>
        </Col>
        {!isPS() && (
          <Col span={12}>
            <PinpointMarker
              pinpoint={getPinpointSceneAndFieldBySheetType({
                pinpointSheetType: props.pinpointSheetType,
                rs: {
                  field: 'rs.snapshot',
                  path: { project: id },
                },
                rsSkippedReview: {
                  field: 'rs.skippedReview.snapshot',
                  path: { project: id },
                },
                ss: {
                  field: 'ss.snapshot',
                  path: { project: id },
                },
                ksInvestment: {
                  field: 'ks.investment.snapshot',
                  path: { project: id },
                },
                ksStandard: {
                  field: 'ks.standard.snapshot',
                  path: { project: id },
                },
                ksLocalGovernment: {
                  field: 'ks.local-government.snapshot',
                  path: { project: id },
                },
              })}
            >
              <Space
                split={
                  <Divider
                    type="vertical"
                    size="md"
                    style={{ backgroundColor: token.colorBorder }}
                  />
                }
              >
                <Text color="secondary" text={t('snapshots-name')} />
                <Text color="secondary" text={snapshot.data?.name} />
              </Space>
            </PinpointMarker>
          </Col>
        )}
      </Row>

      <Row gutter={[24, 12]}>
        {!isPS() && (
          <Col span={12}>
            <PinpointMarker
              pinpoint={getPinpointSceneAndFieldBySheetType({
                pinpointSheetType: props.pinpointSheetType,
                rs: {
                  path: { project: id },
                  field: 'rs.people-in-charge',
                },
                rsSkippedReview: {
                  field: 'rs.skippedReview.people-in-charge',
                  path: { project: id },
                },
                ss: {
                  path: { project: id },
                  field: 'ss.people-in-charge',
                },
                ksInvestment: {
                  path: { project: id },
                  field: 'ks.investment.people-in-charge',
                },
                ksStandard: {
                  path: { project: id },
                  field: 'ks.standard.people-in-charge',
                },
                ksLocalGovernment: {
                  path: { project: id },
                  field: 'ks.local-government.people-in-charge',
                },
              })}
            >
              <DefineTermDescription
                term={t('people-in-charge')}
                items={peopleInChargesNames.map((name) => (
                  <Text key={name} variant="footnote" text={name} />
                ))}
                countValues
                thresholdToViewMore={2}
              />
            </PinpointMarker>
          </Col>
        )}
        <Col span={12}>
          <PinpointMarker
            pinpoint={getPinpointSceneAndFieldBySheetType({
              pinpointSheetType: props.pinpointSheetType,
              rs: {
                path: { project: id },
                field: 'rs.business-manager',
              },
              rsSkippedReview: {
                field: 'rs.skippedReview.business-manager',
                path: { project: id },
              },
              ss: {
                path: { project: id },
                field: 'ss.business-manager',
              },
              ksInvestment: {
                path: { project: id },
                field: 'ks.investment.business-manager',
              },
              ksStandard: {
                path: { project: id },
                field: 'ks.standard.business-manager',
              },
              ksLocalGovernment: {
                path: { project: id },
                field: 'ks.local-government.business-manager',
              },
            })}
          >
            <DefineTermDescription
              term={t('business-manager')}
              items={businessManagerNames.map((name) => (
                <Text key={name} variant="footnote" text={name} />
              ))}
              countValues
              thresholdToViewMore={2}
            />
          </PinpointMarker>
        </Col>
        <Col span={12}>
          <PinpointMarker
            pinpoint={getPinpointSceneAndFieldBySheetType({
              pinpointSheetType: props.pinpointSheetType,
              rs: {
                path: { project: id },
                field: 'rs.other-organization-in-charge',
              },
              rsSkippedReview: {
                field: 'rs.skippedReview.other-organization-in-charge',
                path: { project: id },
              },
              ss: {
                path: { project: id },
                field: 'ss.other-organization-in-charge',
              },
              ksInvestment: {
                path: { project: id },
                field: 'ks.investment.other-organization-in-charge',
              },
              ksStandard: {
                path: { project: id },
                field: 'ks.standard.other-organization-in-charge',
              },
              ksLocalGovernment: {
                path: { project: id },
                field: 'ks.local-government.other-organization-in-charge',
              },
            })}
          >
            <DefineTermDescription
              term={t('other-organization-in-charge')}
              items={nestedOtherResponsibleOrganizationNames.map((name) => (
                <Text key={name} variant="footnote" text={name} />
              ))}
              countValues
              thresholdToViewMore={2}
            />
          </PinpointMarker>
        </Col>
        {!isPS() && (
          <Col span={12}>
            <PinpointMarker
              pinpoint={getPinpointSceneAndFieldBySheetType({
                pinpointSheetType: props.pinpointSheetType,
                rs: {
                  path: { project: id },
                  field: 'rs.related-organizations',
                },
                rsSkippedReview: {
                  field: 'rs.skippedReview.related-organizations',
                  path: { project: id },
                },
                ss: {
                  path: { project: id },
                  field: 'ss.related-organizations',
                },
                ksInvestment: {
                  path: { project: id },
                  field: 'ks.investment.related-organizations',
                },
                ksStandard: {
                  path: { project: id },
                  field: 'ks.standard.related-organizations',
                },
                ksLocalGovernment: {
                  path: { project: id },
                  field: 'ks.local-government.related-organizations',
                },
              })}
            >
              <DefineTermDescription
                term={t('related-organizations')}
                items={nestedRelatedOrganizationNames.map((item, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                  <Text key={index} variant="footnote" text={item} />
                ))}
                countValues
                thresholdToViewMore={2}
              />
            </PinpointMarker>
          </Col>
        )}
        {props.pinpointSheetType === 'KS_STANDARD' && (
          <Col span={12}>
            <PinpointMarker
              pinpoint={{
                path: { project: id },
                field: 'ks.standard.additional-organization-in-charge',
              }}
            >
              <DefineTermDescription
                term={t('additional-organization-in-charge')}
                items={
                  additionalJurisdictionalUsers?.map((item, index) => {
                    const ministryName =
                      getMinistryName({
                        targetId: item.ministryId,
                        ministries: ministries.data,
                      }) ?? FALLBACK_TEXT;

                    return (
                      // biome-ignore lint/suspicious/noArrayIndexKey: biomeをCIに仕込むために暫定的にignore。keyにユニークな値を指定するか適切な理由に書き換えること
                      <Flex vertical key={index}>
                        <Text
                          variant="footnote"
                          text={
                            item.organizationName
                              ? `${ministryName} [${item.organizationName}]`
                              : ministryName
                          }
                        />
                        {!isPS() && (
                          <Flex gap="small">
                            <Text
                              style={{ flexShrink: 0 }}
                              variant="footnote"
                              color="secondary"
                              text={t('people-in-charge')}
                            />
                            <Text
                              variant="footnote"
                              text={item.peopleInChargeNames.join(
                                ITEM_SPLITTER,
                              )}
                            />
                          </Flex>
                        )}
                        <Flex gap="small">
                          <Text
                            style={{ flexShrink: 0 }}
                            variant="footnote"
                            color="secondary"
                            text={t('business-manager')}
                          />
                          <Text
                            variant="footnote"
                            text={item.businessManagerNames.join(ITEM_SPLITTER)}
                          />
                        </Flex>
                      </Flex>
                    );
                  }) ?? []
                }
                countValues
                thresholdToViewMore={2}
              />
            </PinpointMarker>
          </Col>
        )}
      </Row>
    </Flex>
  );
};
